export function humanizeActionFinding(
  actionType: string,
  finding: string,
  competitorName: string
): string {
  if (!finding) return ''

  const lowered = finding.toLowerCase()

  if (actionType === 'creator_scan' || /creator.*scan/i.test(actionType)) {
    const mentionMatch = finding.match(/(\d+)\s*mentions?\s*from\s*(\d+)\s*(?:unique\s*)?creators?/i)
    const verdictMatch = finding.match(/verdict:\s*([\w\s]+?)(?:\.|$)/i)
    const paidRateMatch = finding.match(/paid\s*(?:disclosure\s*)?rate\s*[=:]\s*([\d.]+)%/i)

    if (mentionMatch) {
      const mentions = mentionMatch[1]
      const creators = mentionMatch[2]
      const paidInfo = paidRateMatch
        ? parseFloat(paidRateMatch[1]) < 5
          ? ' Mostly organic — not a paid campaign.'
          : ` Paid disclosure rate: ${paidRateMatch[1]}%.`
        : ''
      const verdict = verdictMatch ? ` Verdict: ${verdictMatch[1].trim()}.` : ''
      return `Creator Scan: ${mentions} mentions from ${creators} creators in 7 days.${paidInfo}${verdict}`
    }
    if (verdictMatch) {
      return `Creator Scan complete. Verdict: ${verdictMatch[1].trim()}.`
    }
    if (paidRateMatch) {
      return `Creator Scan complete. Paid disclosure rate: ${paidRateMatch[1]}%.`
    }
  }

  if (/price.*check|deal.*check/i.test(actionType)) {
    const dropMatch = finding.match(/drop.*?(\d+\.?\d*)%|(\d+\.?\d*)%.*drop/i)
    if (dropMatch) {
      const pct = dropMatch[1] || dropMatch[2]
      return `Price/Deal check result: Analyzed ASINs for ${competitorName}. Largest observed drop vs 30-day average: ${pct}%.`
    }
  }

  if (/keyword/i.test(actionType)) {
    if (/own listing detected/i.test(lowered)) {
      return 'Keyword defense: Own listing confirmed on branded search. No competitor intrusion.'
    }
    if (/manual review/i.test(lowered)) {
      return 'Keyword defense: Competitor intrusion detected - manual review recommended.'
    }
  }

  const cleaned = finding
    .replace(/@[\w.]+(?:\s*\([^)]*\))?/g, '')
    .replace(/Top creators?:.*$/i, '')
    .replace(/:\s*[_.][\w._]{3,}[_.]*/g, '')
    .replace(/,\s*new creator rate\s*=\s*[\d.]+%/gi, '')
    .replace(/\s+/g, ' ')
    .trim()
  return cleaned.length > 200 ? `${cleaned.slice(0, 197)}...` : cleaned
}

export function generateSignalSummary(
  topSignalText: string,
  creatorCount: number,
  paidDisclosureRate: number | null,
  latestBsr: number | null,
  previousBsr: number | null,
  competitorName: string
): string {
  const cleanedText = String(topSignalText ?? '')
    .replace(/@[\w.]+/g, '')
    .replace(/[\w._]+,\s*[\w._]+,/g, '')
    .replace(/:\s*[_.][\w._]{3,}[_.]*/g, '')
    .replace(/\bdays:\s*[_.]?[\w._]+/g, 'days')
    .replace(/\s{2,}/g, ' ')
    .replace(/\.\s*\./g, '.')
    .trim()

  const bsrMatch = cleanedText.match(/#?([\d,]+)\s*(?:→|->|to)\s*#?([\d,]+)/i)
  const bsrFrom = bsrMatch?.[1] ? parseInt(bsrMatch[1].replace(/,/g, ''), 10) : null
  const bsrTo = bsrMatch?.[2] ? parseInt(bsrMatch[2].replace(/,/g, ''), 10) : null

  const fallbackFrom = previousBsr !== null && Number.isFinite(previousBsr) ? Math.round(previousBsr) : null
  const fallbackTo = latestBsr !== null && Number.isFinite(latestBsr) ? Math.round(latestBsr) : null
  const resolvedFrom = bsrFrom ?? fallbackFrom
  const resolvedTo = bsrTo ?? fallbackTo

  const multMatch = cleanedText.match(/([\d.]+)\s*(?:x|×)/i)
  const multiplier = multMatch?.[1] ? parseFloat(multMatch[1]) : null
  const resolvedCreatorCount = Number.isFinite(creatorCount) ? Math.max(0, Math.round(creatorCount)) : 0

  const paidSummary =
    paidDisclosureRate === null || !Number.isFinite(paidDisclosureRate)
      ? ''
      : paidDisclosureRate < 5
        ? 'Mostly organic - not a paid campaign.'
        : `${paidDisclosureRate.toFixed(0)}% paid - active ad spend detected.`

  if (multiplier && multiplier >= 1.2 && resolvedCreatorCount > 0) {
    return `Creator activity ~${multiplier.toFixed(1)}x normal with ${resolvedCreatorCount} creators in 7 days. ${paidSummary}`.trim()
  }

  if (resolvedFrom && resolvedTo && resolvedTo < resolvedFrom) {
    return `Amazon rank improved from #${resolvedFrom.toLocaleString('en-US')} to #${resolvedTo.toLocaleString('en-US')} - stronger sales momentum.`
  }

  if (resolvedCreatorCount > 0) {
    return `${resolvedCreatorCount} creators mentioned ${competitorName} in 7 days. ${paidSummary}`.trim()
  }

  if (cleanedText) {
    return cleanedText
  }

  return `Monitoring ${competitorName} - no dominant signal this week.`
}
